<template>
    <form class="jgile-avatar" data-test="avatar-form" ref="form">
        <div class="image-placeholder">
            <span role="img" data-test="avatar-preview" class="profile-photo-preview" :style="previewStyle"></span>
        </div>
        <div>
            <div class="jgile-upload-avatar">
                <input :disabled="form.busy" :name="name" @change="update" class="jgile-uploader-control"
                       data-test="avatar-input" ref="photo" type="file"/>
                <div class="btn btn-secondary pointer">Update {{ label }}</div>
            </div>
            <div class="invalid-feedback d-block text-center" v-if="form.errors.has(name)">
                {{ form.errors.get(name) }}
            </div>
            <small class="jgile-avatar-allowed-types">Allowed file types: jpg,png,gif</small>
            <button class="remove-photo btn btn-plain btn-link d-block mt-3" @click.prevent="removePhoto">Remove
                {{ label }}
            </button>
        </div>
        <b-modal size="lg" id="cropModal" v-model="showModal" :title="'Crop ' + label" no-close-on-backdrop>
            <div class="d-block" style="max-height: 500px;">
                <img v-if="cropImg" :src="cropImg" ref="cropImg" class="opacity-0"/>
            </div>
            <template slot="modal-footer">
                <button type="button" class="btn btn-sm btn-primary" @click="zoomIn">
                    <span class="sr-only">Zoom In</span>
                    <i class="fa fa-search-plus"></i>
                </button>
                <button type="button" class="mr-auto btn btn-sm btn-primary" @click="zoomOut">
                    <span class="sr-only">Zoom Out</span>
                    <i class="fa fa-search-minus"></i>
                </button>
                <!--                <button type="button" class="btn btn-cancel" data-dismiss="modal" @click.prevent="showModal = !showModal">-->
                <!--                    Cancel-->
                <!--                </button>-->
                <button type="button" class="btn btn-secondary" data-test="avatar-crop-save" @click.prevent="saveCrop">
                    Save
                </button>
            </template>
        </b-modal>
    </form>
</template>

<script>

export default {
    props: {
        storeUrl: {
            type: String,
            required: true,
        },
        removeUrl: {
            type: String,
            required: true,
        },
        name: {
            type: String,
            default: 'photo',
        },
        label: {
            default: 'Photo',
        },
        options: {},
        value: {},
    },
    data() {
        return {
            form: new SparkForm({}),
            cropImg: null,
            cropper: null,
            showModal: false,
        };
    },
    computed: {
        previewStyle() {
            return `background-image: url(${this.value})`;
        },
    },
    methods: {
        zoomIn() {
            this.cropper.zoom(0.1);
        },
        zoomOut() {
            this.cropper.zoom(-0.1);
        },
        imgLoaded(url) {
            this.cropImg = url;
            this.showModal = true;

            this.$nextTick(() => {
                try {
                    if (this.$refs.cropImg) {
                        // Destroy existing cropper if it exists
                        if (this.cropper) {
                            this.cropper.destroy();
                        }

                        this.cropper = new Cropper(this.$refs.cropImg, {
                            initialAspectRatio: 1,
                            aspectRatio: 1,
                            viewMode: 0,
                            movable: false,
                            rotatable: false,
                            scalable: false,
                            ready: function () {
                                console.log('Cropper initialized successfully');
                            },
                            error: function (error) {
                                console.error('Cropper error:', error);
                            },
                        });
                    } else {
                        console.error('Crop image reference not found');
                    }
                } catch (error) {
                    console.error('Failed to initialize cropper:', error);
                    this.form.setErrors({
                        [this.name]: ['Failed to initialize image editor. Please try again.'],
                    });
                }
            });
        },
        update() {
            let files = this.$refs.photo.files;
            let file;

            // Clear any previous errors
            if (this.form.errors && this.form.errors.clear) {
                this.form.errors.clear();
            }

            if (files && files.length > 0) {
                file = files[0];

                // Validate file size (limit to 10MB)
                if (file.size > 10 * 1024 * 1024) {
                    this.form.setErrors({
                        [this.name]: ['File size must be less than 10MB.'],
                    });
                    return;
                }

                // Validate file type
                const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
                if (!allowedTypes.includes(file.type)) {
                    this.form.setErrors({
                        [this.name]: ['File must be jpg, png, or gif.'],
                    });
                    return;
                }

                console.log('File selected:', {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                });

                if (URL && URL.createObjectURL) {
                    try {
                        this.imgLoaded(URL.createObjectURL(file));
                    } catch (error) {
                        console.error('Error creating object URL:', error);
                        this.fallbackFileReader(file);
                    }
                } else {
                    this.fallbackFileReader(file);
                }
            }
        },

        fallbackFileReader(file) {
            if (FileReader) {
                const reader = new FileReader();
                const self = this;

                reader.onload = function (e) {
                    self.imgLoaded(e.target.result);
                };

                reader.onerror = function (error) {
                    console.error('FileReader error:', error);
                    self.form.setErrors({
                        [self.name]: ['Failed to read file. Please try again.'],
                    });
                };

                reader.readAsDataURL(file);
            } else {
                this.form.setErrors({
                    [this.name]: ['File reading not supported in this browser.'],
                });
            }
        },

        saveCrop() {
            const self = this;
            const initialAvatarURL = this.value;
            let canvas;

            this.showModal = false;

            if (!this.cropper) {
                console.error('Cropper not initialized');
                return;
            }

            try {
                canvas = this.cropper.getCroppedCanvas({
                    width: 300,
                    height: 300,
                    fillColor: '#ffffff',
                    imageSmoothingEnabled: true,
                    imageSmoothingQuality: 'high',
                });

                if (!canvas) {
                    console.error('Failed to create canvas');
                    return;
                }

                // Show temporary preview
                self.form.startProcessing();

                // Convert canvas to blob with better error handling
                canvas.toBlob(function (blob) {
                    if (!blob) {
                        console.error('Failed to create blob from canvas');
                        self.$emit('input', initialAvatarURL);
                        self.form.finishProcessing();
                        return;
                    }

                    console.log('Blob created:', {
                        size: blob.size,
                        type: blob.type,
                    });

                    // Create FormData with proper file structure
                    const formData = new FormData();
                    formData.append(self.name, blob, `${self.name}.jpg`);

                    // Log FormData contents for debugging
                    for (let pair of formData.entries()) {
                        console.log('FormData:', pair[0], pair[1]);
                    }

                    // Send request with proper headers
                    axios.post(self.storeUrl, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                        timeout: 30000,
                    }).then(
                        (response) => {
                            self.$emit('input', response.data?.data?.photo_url);
                            Bus.$emit('updateUser');
                            Bus.$emit('updateTeam');
                            Bus.$emit('updateTeams');
                            self.form.finishProcessing();
                        },
                        (error) => {
                            console.error('Upload error:', error);
                            self.$emit('input', initialAvatarURL);

                            if (error.response && error.response.data && error.response.data.errors) {
                                self.form.setErrors(error.response.data.errors);
                            } else {
                                self.form.setErrors({
                                    [self.name]: ['Upload failed. Please try again.'],
                                });
                            }

                            self.form.finishProcessing();
                        },
                    );
                }, 'image/jpeg', 0.9);

            } catch (error) {
                self.$emit('input', initialAvatarURL);
                self.form.finishProcessing();
            }
        },
        removePhoto() {
            let self = this;
            this.form.startProcessing();

            axios.post(this.removeUrl, {[this.name]: null}, {
                headers: {
                    'Content-Type': 'application/json',
                },
                timeout: 10000, // 10 second timeout
            }).then(
                (response) => {
                    if (response.data && response.data.hasOwnProperty('photo_url')) {
                        self.$emit('input', response.data.photo_url);
                    } else {
                        self.$emit('input', null);
                    }

                    Bus.$emit('updateUser');
                    Bus.$emit('updateTeam');
                    Bus.$emit('updateTeams');

                    self.$refs.form.reset();
                    self.form.finishProcessing();
                },
                (error) => {
                    if (error.response && error.response.data && error.response.data.errors) {
                        self.form.setErrors(error.response.data.errors);
                    } else {
                        self.form.setErrors({
                            [self.name]: ['Failed to remove photo. Please try again.'],
                        });
                    }

                    self.form.finishProcessing();
                },
            );
        },
    },
};
</script>
